import { NavLink } from "react-router";
import { useEffect } from "react";
import { usePermission } from "hooks/use-permissions";

import { SubMenuItemType } from "models/header/menuItems";
import { UserInfo } from "models/user/user";
import { useGlobalContext } from "app/store/GlobalContext";
import Badge from "components/badge/Badge";
import { Icon } from "components/icon/Icon";
import { captureEvent } from "components/posthog/customEventCapture";

import ExternalUserCustomerMenu from "./dropdownMenu/ExternalUserCustomerMenu";
import CustomerNavLinks from "../CustomerNavLinks";
import UserProfileDropdown from "./dropdownMenu/UserProfileDropdown";

import "./MainMenuFullscreen.scss";

type Props = {
  secondaryMenuItems?: SubMenuItemType[];
  toggleFullScreenMenu: boolean;
  setToggleFullScreenMenu: (value: boolean) => void;
  onLogout: () => void;
  userInfo: UserInfo | null | undefined;
  customers: any[];
  handleChangeCustomer: (customerId: string) => void;
};

export default function MainMenuFullscreen({
  secondaryMenuItems = [],
  toggleFullScreenMenu,
  setToggleFullScreenMenu,
  onLogout,
  userInfo,
  customers,
  handleChangeCustomer
}: Props) {
  const { handleNavigation } = useGlobalContext();
  const isInternalUser = usePermission("isInternal");

  const classes = toggleFullScreenMenu
    ? "fullscreenMenu fullscreenMenuShow"
    : "fullscreenMenu";

  const closeMenu = () => setToggleFullScreenMenu(false);

  // Lock body scroll when menu is open
  useEffect(() => {
    if (toggleFullScreenMenu) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [toggleFullScreenMenu]);

  return (
    <>
      <div className={classes}>
        {/* Header section */}

        {/* Main content */}
        <nav className="fullscreenMenuContent">
          {/* menu items */}
          {secondaryMenuItems.length > 0 && (
            <>
              <div className="fullscreenMenuSection">
                {secondaryMenuItems.map((item, index) => (
                  <NavLink
                    key={index}
                    to={item.href}
                    onClick={(e) => {
                      if (item.disabled) {
                        e.preventDefault();
                        return;
                      }
                      handleNavigation({
                        url: item.href,
                        eventTitle: item.title,
                        e: e
                      });
                      closeMenu();
                    }}
                    title={item.text}
                    className={`fullscreenMenuNavLink ${item.disabled ? "disabled" : ""}`}
                  >
                    <span>{item.title}</span>
                    {typeof item.count === "number" && item.count > 0 && (
                      <Badge className="badge" count={item.count} hideCount />
                    )}
                    {item.notice && (
                      <Badge className="badge" notice={item.notice} />
                    )}
                  </NavLink>
                ))}
              </div>
              <div className="fullscreenMenuDivider" />
            </>
          )}
          {/* Customer navigation section - only for internal users */}
          {isInternalUser && (
            <CustomerNavLinks
              handleChangeCustomerNavigation={handleChangeCustomer}
              isOnMobile
            />
          )}
          {/* External user with multiple customers */}
          {!isInternalUser && customers.length > 1 && (
            <>
              <div className="fullscreenMenuSection">
                <ExternalUserCustomerMenu
                  handleChangeCustomer={(customerId) => {
                    handleChangeCustomer(customerId);
                    closeMenu();
                  }}
                />
              </div>
              <div className="fullscreenMenuDivider" />
            </>
          )}
          <UserProfileDropdown onCloseMenu={closeMenu} onLogout={onLogout} />
        </nav>
      </div>

      <button
        className="fullscreenMenuToggle"
        onClick={() => {
          setToggleFullScreenMenu(!toggleFullScreenMenu);
          captureEvent("Main menu full screen click");
        }}
      >
        {toggleFullScreenMenu ? <Icon name="times" /> : <Icon name="bars" />}
      </button>
    </>
  );
}
